import React, { useEffect, useState, useContext, Fragment, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useRecoilValue } from 'recoil';
import { Menu, Transition, Dialog } from '@headlessui/react'
import {
    ArrowPathIcon,
    ChevronDownIcon,
    TrashIcon,
    RectangleGroupIcon,
} from '@heroicons/react/20/solid'
import { DocumentMagnifyingGlassIcon, ExclamationTriangleIcon, XMarkIcon, PhoneIcon } from '@heroicons/react/24/outline';
import ExcelImport, { ImportSummary, MissingEmails, importErrorText, buildMissingItems, applyEmailFixes, unresolvedMissing, typedEmails } from '../../../components/admin/excelimport.component';

//import services
import MeetingService from "../../../services/meeting.service";
import { socket, SocketContext } from "../../../services/socket.service";
import SignService from '../../../services/sign.service';
import meetingAdminService from '../../../services/meeting.admin.service';
import userAdminService from '../../../services/user.admin.service';


//import components
import Spinner from '../../../components/ui/spinner.component';
import UserSearch from '../../../components/admin/usersearch.component';

//import datastores
import { meetingConnectionState } from '../../../data/meeting.atom';
import { meState } from '../../../data/me.atom';



function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

// Columns the building file import looks for; match holds header names that select a column automatically.
const MEETING_FIELDS = [
    { key: 'name', label: 'Naam (stemhouder)', required: true, match: ['stemhouder', 'contact naam', 'naam', 'name', 'contact'] },
    { key: 'email', label: 'E-mailadres', required: true, match: ['email', 'e-mail', 'e-mailadres', 'mail'] },
    { key: 'shares', label: 'Aandelen (quotiteit)', required: false, match: ['quotiteit', 'aandelen', 'shares', 'quotiteiten'] },
    { key: 'telephone', label: 'GSM', required: false, match: ['gsm', 'mobiel', 'mobile', 'telefoon', 'tel', 'phone'] },
    { key: 'telephone2', label: 'Telefoon', required: false, match: ['tel', 'telefoon', 'phone'] },
    { key: 'language', label: 'Taal', required: false, match: ['taal', 'language', 'lang'] },
];

export default function AdminMeetingUsersView() {
    const meetingConnection = useRecoilValue(meetingConnectionState);
    const me = useRecoilValue(meState);
    const [invitees, setInvitees] = useState([]);
    const [reloadInvites, setReloadInvites] = useState(0);
    const [viewers, setViewers] = useState([]);
    const [users, setUsers] = useState([]);
    const [signs, setSigns] = useState([]);
    const [meeting, setMeeting] = useState({});

    const [reload, setReload] = useState(0);


    const [excelModalOpen, setExcelModalOpen] = useState(false)
    const [uploading, setUploading] = useState(false);
    const [importRows, setImportRows] = useState(null);
    const [importStep, setImportStep] = useState('file'); // file, preview, done
    const [importResult, setImportResult] = useState(null);
    const [missingItems, setMissingItems] = useState([]); // names without a usable e-mail in the last check
    const [emailFixes, setEmailFixes] = useState({});
    const [invalidFixes, setInvalidFixes] = useState([]);

    const [noDocusignContract, setNoDocusignContract] = useState(false)
    const [deleteCandidate, setDeleteCandidate] = useState(null)
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [phoneModalOpen, setPhoneModalOpen] = useState(false)
    const [selectedPhone, setSelectedPhone] = useState('')
    const [selectedName, setSelectedName] = useState('')
    const [currentCallAttendId, setCurrentCallAttendId] = useState(null)
    const [currentCallUserId, setCurrentCallUserId] = useState(null)
    const [myCallAttempts, setMyCallAttempts] = useState({})
    const myCallAttemptsRef = useRef({})
    
    // Refs for immediate access to modal state
    const phoneModalOpenRef = useRef(false)
    const currentCallAttendIdRef = useRef(null)
    
    const [statistics, setStatistics] = useState({
        online: 0,
        present: 0,
        not: 0,
        no_response: 0,
        total_shares: 0,
        total_invitees: 0
    })
    const [loadingStatistics, setLoadingStatistics] = useState(false)
    const [currentFilter, setCurrentFilter] = useState('investeerders')

    const { id } = useParams();
    const socketprovider = useContext(SocketContext);

    useEffect(() => {
        MeetingService.getInvitees(id)
            .then(response => {
                // Add initial call status (green by default, red if called, orange if called in another meeting today)
                const inviteesWithCallStatus = response.data.map(invitee => ({
                    ...invitee,
                    call_status: invitee.called ? 'red' : (invitee.previous_call ? 'orange' : 'green')
                }));
                setInvitees(sortData(inviteesWithCallStatus));
            })
            .catch(err => {
                console.log(err);
            }
            );
    }, [reloadInvites]);

    useEffect(() => {
        meetingAdminService.getMeeting(id)
            .then(response => {
                setMeeting(response.data);
            })
            .catch(err => {
                console.log(err);
            });
    }, []);

    useEffect(() => {
        loadStatistics(currentFilter);
    }, []);

    function loadStatistics(filter = 'investeerders') {
        setLoadingStatistics(true);
        meetingAdminService.getInviteesStatistics(id, filter)
            .then(response => {
                setStatistics(response.data);
            })
            .catch(err => {
                console.log("Error loading statistics:", err);
                toast.error("Kon statistieken niet laden.");
            })
            .finally(() => {
                setLoadingStatistics(false);
            });
    }

    useEffect(() => {
        SignService.getAdminSigns(id)
            .then(response => {
                console.log(response.data)
                setSigns(response.data);
            })
            .catch(err => {
                console.log(err.message)
                toast.error("Er is iets fout gegaan.");
            });
    }, [reload]);

    // Function to search users via API
    function searchUsers(query) {
        if (!query || query.length < 2) {
            setUsers([]);
            return;
        }
        
        meetingAdminService.searchUsers(query)
            .then(response => {
                // Handle the new paginated response format
                const users = response.data.users || response.data;
                setUsers(
                    users.filter(
                        (user) =>
                            !invitees.map(invitee => invitee.user_attend.id)
                                .includes(user.id)
                            && user.admin == false
                    )
                );
            })
            .catch(err => {
                console.log("Error searching users:", err);
                setUsers([]);
            });
    }

    useEffect(() => {
        // Redirect to meetings overview if no live_token (page refresh scenario)
        if (!meetingConnection.live_token) {
            window.location.href = '/admin/meetings';
            return;
        }
        
        // Cleanup old call attempts every minute to prevent memory leaks
        const cleanupInterval = setInterval(() => {
            const now = Date.now();
            setMyCallAttempts(prev => {
                const updated = {};
                Object.entries(prev).forEach(([attendId, timestamp]) => {
                    // Keep timestamps that are less than 5 minutes old
                    if (now - timestamp < 5 * 60 * 1000) {
                        updated[attendId] = timestamp;
                    }
                });
                return updated;
            });
        }, 60000); // Run every minute
        
        if (meetingConnection.live_token) {
            socketprovider.connect("admin", meetingConnection.live_token);

            socketprovider.socket.on('admin', function (data) {
            setViewers([]);
            for (var i = 0; i < data.length; i++) {
                if (data[i].meeting_id == id) {
                    const viewer = data[i]['value']
                    setViewers(viewers => [...viewers, viewer]);
                }
            }
        });

        socketprovider.socket.on('rsvp', function (data) {
            if (data.meeting_id == id) {
                setReloadInvites(reloadInvites => reloadInvites + 1)
                //temporary fix to reload invites, should adapt the invitees state
            }
        });

        //LIVE COM FOR SIGN
        socketprovider.socket.on('sign', function (data) {
            if (data.envelope_id) {
                setReload(reload => reload + 1);
            }
        });

        //LIVE COM FOR COMMENT
        socketprovider.socket.on('usercomment', function (data) {
            if (data.meeting_id == id) {
                setInvitees(invitees => invitees.map(invitee => {
                    if (invitee.id == data.attend_id) {
                        invitee.comment = data.message;
                    }
                    return invitee;
                }));
            }
        });

        //LIVE COM FOR CALL STATUS WITH RACE CONDITION PREVENTION  
        socketprovider.socket.on('call_status_update', function (data) {
            if (data.meeting_id == id) {
                // 1. Always update the button status first
                setInvitees(invitees => invitees.map(invitee => {
                    if (invitee.id == data.attend_id) {
                        invitee.call_status = data.call_status;
                    }
                    return invitee;
                }));
                
                // 2. Check race condition: if I have modal open for this invitee and it's from another admin
                const currentModalOpen = phoneModalOpenRef.current;
                const currentAttendId = currentCallAttendIdRef.current;
                
                if (currentModalOpen && currentAttendId === data.attend_id && data.admin_id !== me.id) {
                    const myTimestamp = myCallAttemptsRef.current[data.attend_id];
                    const theirTimestamp = data.click_timestamp;
                    
                    // If their timestamp is earlier than mine, I lose - close modal
                    if (myTimestamp && theirTimestamp < myTimestamp) {
                        setPhoneModalOpen(false);
                        setCurrentCallAttendId(null);
                        setCurrentCallUserId(null);
                        
                        // Update refs immediately
                        phoneModalOpenRef.current = false;
                        currentCallAttendIdRef.current = null;
                        
                        toast.info(`${data.admin_name || 'Andere admin'} was eerder , bel iemand anders.`);
                    }
                }
            }
        });


            return () => {
                socketprovider.socket.off('admin');
                socketprovider.socket.off('rsvp');
                socketprovider.socket.off('sign');
                socketprovider.socket.off('usercomment');
                socketprovider.socket.off('call_status_update');
                socketprovider.disconnect();
                clearInterval(cleanupInterval);
            }
        }
    }, [meetingConnection.live_token]);

    function sortData(data) {
        const sorted =
            [
                ...data.filter(invitee => invitee.rsvp == "online" && invitee.rsvp != "present" && invitee.rsvp != "proxy" && invitee.rsvp != "not" && invitee.rsvp != ""),
                ...data.filter(invitee => invitee.rsvp == "proxy" && invitee.rsvp != "online" && invitee.rsvp != "present" && invitee.rsvp != "not" && invitee.rsvp != ""),
                ...data.filter(invitee => invitee.rsvp == "present" && invitee.rsvp != "online" && invitee.rsvp != "proxy" && invitee.rsvp != "not" && invitee.rsvp != ""),
                ...data.filter(invitee => invitee.rsvp == "not" && invitee.rsvp != "online" && invitee.rsvp != "proxy" && invitee.rsvp != "present" && invitee.rsvp != ""),
                ...data.filter(invitee => invitee.rsvp != "online" && invitee.rsvp != "present" && invitee.rsvp != "not" && invitee.rsvp == ""),
            ]
        return sorted;
    }

    function inviteUser(e) {
        meetingAdminService.inviteUser(id, e.id)
            .then(response => {
                setInvitees(invitees => [...invitees, { id: response.data.id, user_attend: e }]);
                toast.success(e.firstname + " " + e.lastname + " is uitgenodigd voor de AV.");
            }
            )
            .catch(err => {
                toast.error("Er is iets fout gegaan.");
            });
    }

    function deleteUserInvite(id) {
        meetingAdminService.deleteUserInvite(id)
            .then(response => {
                setInvitees(prev => prev.filter(invitee => invitee.id !== id));
                setSigns(prev => prev.filter(sign => sign.signage_attend?.id !== id));
                toast.success("De gebruiker is verwijderd.");
            })
            .catch(err => {
                toast.error("Er is iets fout gegaan.");
            });
    }

    // Deleting a participant who signed a contract also removes the link to that
    // contract, so ask first. Everyone else is deleted straight away, as before.
    function requestDeleteInvite(id) {
        const signed = signs.filter(sign => sign.signage_attend?.id === id);
        if (signed.length === 0) {
            deleteUserInvite(id);
            return;
        }
        const invitee = invitees.find(item => item.id === id);
        setDeleteCandidate({
            id,
            name: invitee ? `${invitee.user_attend.firstname} ${invitee.user_attend.lastname}` : 'Deze deelnemer',
            contracts: signed.map(sign => sign.contract_signage?.name).filter(Boolean),
        });
        setDeleteDialogOpen(true);
    }

    function confirmDeleteInvite() {
        setDeleteDialogOpen(false);
        deleteUserInvite(deleteCandidate.id);
    }

    function adminUpdateRSVP(user_id, rsvp) {
        meetingAdminService.updateRSVP(id, user_id, rsvp)
            .then(response => {
                toast.success("De RSVP is aangepast.");
            })
            .catch(err => {
                toast.error("Er is iets fout gegaan.");
            });
    }

    function redirectEnvelope(id, firstname, lastname) {
        if (id == "548795") {
            setNoDocusignContract(true);
        } else {
            SignService.downloadPdf(id)
                // is blob, download to client device
                .then(response => {
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', 'document_' + firstname + '_' + lastname + '.pdf');
                    document.body.appendChild(link);
                    link.click();
                })
                .catch(err => {
                    toast.error("Er is iets fout gegaan.");
                });
        }
    }

    function refreshUser(id) {
        meetingAdminService.refreshUser(id)
            .then(response => {
                toast.success(response.data)
            })
            .catch(err => {
                toast.error(err.message)
            })
    }

    function sendMessage(message, attend_id, save_db) {
        console.log(message, attend_id, save_db);
        setInvitees(invitees => invitees.map(invitee => {
            if (invitee.id == attend_id) {
                invitee.comment = message;
            }
            return invitee;
        }));

        socketprovider.socket.emit('usercomment', { message: message, attend_id: attend_id, meeting_id: id, save_db: save_db });
    }


    function checkImport() {
        if (!importRows || importRows.length === 0) {
            toast.error("Er is geen bestand geselecteerd of niet alle kolommen zijn gekozen.");
            return;
        }
        setUploading(true);
        userAdminService.importMeetingFile(id, importRows, true)
            .then(response => {
                setImportResult(response.data);
                setMissingItems(buildMissingItems(response.data));
                setEmailFixes({});
                setInvalidFixes([]);
                setImportStep('preview');
            })
            .catch(err => {
                console.log(err);
                toast.error(importErrorText(err));
            })
            .finally(() => setUploading(false));
    }

    // Writes the typed e-mail addresses into the rows, checks them with a dry
    // run and only then imports.
    function runImport() {
        if (unresolvedMissing(missingItems, emailFixes).length > 0) {
            toast.error("Vul de ontbrekende e-mailadressen in of vink Overslaan aan.");
            return;
        }
        const rows = applyEmailFixes(importRows, missingItems, emailFixes);
        setUploading(true);
        userAdminService.importMeetingFile(id, rows, true)
            .then(check => {
                const typed = typedEmails(emailFixes);
                const rejected = check.data.invalid_email.map(item => item.email).filter(email => typed.includes(email));
                if (rejected.length > 0) {
                    setImportResult(check.data);
                    setInvalidFixes(rejected);
                    toast.error("Een ingevuld e-mailadres is ongeldig.");
                    return null;
                }
                return userAdminService.importMeetingFile(id, rows, false);
            })
            .then(response => {
                if (!response) return;
                setImportResult(response.data);
                setImportStep('done');
                toast.success("Gebruikers zijn uitgenodigd.");
                setReloadInvites(reload => reload + 1);
                loadStatistics(currentFilter);
            })
            .catch(err => {
                console.log(err);
                toast.error(importErrorText(err));
            })
            .finally(() => setUploading(false));
    }

    function cancelExcelUpload() {
        setExcelModalOpen(false);
        setImportRows(null);
        setImportStep('file');
        setImportResult(null);
        setMissingItems([]);
        setEmailFixes({});
        setInvalidFixes([]);
    }

    function adminUpdateShares(attend_id, shares) {
        console.log(id, attend_id, shares);
        meetingAdminService.updateShares(id, attend_id, shares)
            .then(response => {
                toast.success("Het aantal aandelen is aangepast.");
            })
            .catch(err => {
                toast.error("Er is iets fout gegaan.");
            });
    }

    function adminUpdateSyndesk(attend_id, syndesk) {
        meetingAdminService.updateSyndesk(id, attend_id, syndesk)
            .then(response => {
                toast.success("Syndesk status is aangepast.");
            })
            .catch(err => {
                toast.error("Er is iets fout gegaan.");
            });
    }

    function handleFilterChange(newFilter) {
        setCurrentFilter(newFilter);
        loadStatistics(newFilter);
    }

    function openPhoneModal(phoneNumber, attendId, userId, name) {
        setSelectedPhone(phoneNumber);
        setSelectedName(name);
        setCurrentCallAttendId(attendId);
        setCurrentCallUserId(userId);
        setPhoneModalOpen(true);
        
        // Update refs immediately for socket listener access
        phoneModalOpenRef.current = true;
        currentCallAttendIdRef.current = attendId;
    }

    function handleCallStart(attend_id, user_id, phoneNumber, name) {
        const clickTimestamp = Date.now();
        
        // Track our attempt with timestamp (both state and ref for immediate access)
        setMyCallAttempts(prev => ({...prev, [attend_id]: clickTimestamp}));
        myCallAttemptsRef.current = {...myCallAttemptsRef.current, [attend_id]: clickTimestamp};
        
        // Optimistic update - immediately change button to red
        setInvitees(invitees => invitees.map(invitee => {
            if (invitee.id === attend_id) {
                invitee.call_status = 'red';
            }
            return invitee;
        }));

        // Emit socket event if connected with timestamp and admin info
        if (socketprovider.socket) {
            socketprovider.socket.emit('user_call_start', {
                attend_id: attend_id,
                user_id: user_id,
                meeting_id: id,
                click_timestamp: clickTimestamp,
                admin_id: me.id,
                admin_name: `${me.firstname} ${me.lastname}`
            });
        }

        // Open modal
        openPhoneModal(phoneNumber, attend_id, user_id, name);
    }

    function handleCallReset(attend_id, user_id) {
        // Optimistic update - immediately change button to green
        setInvitees(invitees => invitees.map(invitee => {
            if (invitee.id === attend_id) {
                invitee.call_status = 'green';
            }
            return invitee;
        }));

        // Emit socket event if connected with admin info
        if (socketprovider.socket) {
            socketprovider.socket.emit('user_call_reset', {
                attend_id: attend_id,
                user_id: user_id,
                meeting_id: id,
                admin_id: me.id,
                admin_name: `${me.firstname} ${me.lastname}`
            });
        }
    }

    function handlePhoneModalCancel() {
        // Reset call status when canceling
        if (currentCallAttendId && currentCallUserId) {
            handleCallReset(currentCallAttendId, currentCallUserId);
        }
        
        // Clear our call attempt record
        if (currentCallAttendId) {
            setMyCallAttempts(prev => {
                const updated = {...prev};
                delete updated[currentCallAttendId];
                return updated;
            });
        }
        
        // Close modal
        setPhoneModalOpen(false);
        setCurrentCallAttendId(null);
        setCurrentCallUserId(null);
        setSelectedName('');
    }

    function handleCallResult(result) {
        // Map result to comment text
        const commentMap = {
            'voicemail': 'Voicemail ingesproken',
            'afwezig': 'Afwezig - komt niet',
            'aanwezig': 'Aanwezig - bevestigd'
        };

        // Add comment via socket (like existing comment system)
        const baseComment = commentMap[result] || `Gebeld: ${result}`;
        const callerName = me.firstname && me.lastname ? `${me.firstname} ${me.lastname}` : 'Admin';
        const comment = `${baseComment} (gebeld door ${callerName})`;
        if (socketprovider.socket && currentCallAttendId) {
            // Use existing sendMessage function to add comment
            sendMessage(comment, currentCallAttendId, true);
        }

        // Special handling for "afwezig" - also update RSVP status
        if (result === 'afwezig' && currentCallUserId) {
            adminUpdateRSVP(currentCallUserId, 'not');
        }

        // Keep call status as red (don't reset)
        // Close modal
        setPhoneModalOpen(false);
        setCurrentCallAttendId(null);
        setCurrentCallUserId(null);
        setSelectedName('');
    }

    return (
        <main className="flex-1">
            <div className="py-6">
                <div className="flex mx-auto px-4 sm:px-6 md:px-8">
                    <div className="w-1/2">
                        <div>
                            <h1 className="text-2xl font-semibold text-gray-900">Genodigden</h1>
                        </div>
                        <div>
                            <p>Hieronder een lijst van genodigden van deze AV</p>
                        </div>
                        
                        {/* Compact Statistics */}
                        <div className="bg-gradient-to-r from-gray-50 to-white rounded-xl p-4 border border-gray-200 w-full max-w-lg mt-4">
                            <div className="flex items-center justify-between mb-1">
                                <h3 className="text-sm font-medium text-gray-700">Statistieken</h3>
                                <button
                                    onClick={() => loadStatistics(currentFilter)}
                                    disabled={loadingStatistics}
                                    className="inline-flex items-center text-xs px-2 py-1 text-gray-600 hover:text-gray-900 focus:outline-none"
                                >
                                    <ArrowPathIcon className={`h-3 w-3 mr-1 ${loadingStatistics ? 'animate-spin' : ''}`} />
                                    Vernieuwen
                                </button>
                            </div>
                            {currentFilter !== 'all' && (
                                <p className="text-xs text-gray-500 mb-2">
                                    Gefilterd voor: <span className="font-medium text-indigo-600">
                                        {currentFilter === 'investeerders' ? 'Investeerders' : 
                                         currentFilter === 'medewerkers' ? 'Medewerkers' : 'Alle genodigden'}
                                    </span>
                                </p>
                            )}
                            <div className="grid grid-cols-3 gap-4 text-center">
                                <div>
                                    <div className="text-lg font-semibold text-blue-600">{statistics.online}</div>
                                    <div className="text-xs text-gray-500">Online</div>
                                </div>
                                <div>
                                    <div className="text-lg font-semibold text-green-600">{statistics.present}</div>
                                    <div className="text-xs text-gray-500">Fysiek</div>
                                </div>
                                <div>
                                    <div className="text-lg font-semibold text-red-600">{statistics.not}</div>
                                    <div className="text-xs text-gray-500">Afwezig</div>
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-4 text-center mt-3 pt-3 border-t border-gray-200">
                                <div>
                                    <div className="text-lg font-semibold text-gray-600">{statistics.no_response}</div>
                                    <div className="text-xs text-gray-500">Geen reactie</div>
                                </div>
                                <div>
                                    <div className="text-lg font-semibold text-purple-600">{statistics.total_invitees}</div>
                                    <div className="text-xs text-gray-500">Totaal</div>
                                </div>
                                <div>
                                    <div className="text-lg font-semibold text-indigo-600">{statistics.total_shares}</div>
                                    <div className="text-xs text-gray-500">Aandelen</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="w-1/2 flex flex-col justify-start">
                        <div className="rounded-lg border-2 border-dashed border-gray-300 px-6 py-4 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 mt-4 ml-4">
                            <div className="mx-auto">
                                <p>Nodig een gebruiker uit voor deze AV of{" "}
                                    <button
                                        className="ml-1 inline-flex items-center rounded border border-transparent bg-indigo-600 px-1.5 py-1 text-xs font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                        onClick={() => setExcelModalOpen(true)}
                                    >
                                        importeer het gebouwbestand
                                    </button>
                                </p>
                            </div>
                            <div className="mx-auto my-4">
                                <UserSearch
                                    people={users}
                                    inviteUser={inviteUser}
                                    onSearch={searchUsers}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mx-auto px-4 sm:px-6 md:px-8">
                    <TableComponent
                        invitees={invitees}
                        setInvitees={setInvitees}
                        viewers={viewers}
                        signs={signs}
                        redirectEnvelope={redirectEnvelope}
                        sendMessage={sendMessage}
                        refreshUser={refreshUser}
                        deleteUserInvite={requestDeleteInvite}
                        adminUpdateRSVP={adminUpdateRSVP}
                        adminUpdateShares={adminUpdateShares}
                        adminUpdateSyndesk={adminUpdateSyndesk}
                        isVME={meeting.building_meeting?.type === 'VME'}
                        openPhoneModal={openPhoneModal}
                        handleCallStart={handleCallStart}
                        handleCallReset={handleCallReset}
                        currentFilter={currentFilter}
                        onFilterChange={handleFilterChange}
                    />
                </div>
            </div>

            {/* MODAL EXCEL UPLOAD */}
            <Transition.Root show={excelModalOpen} as={Fragment}>
                <Dialog as="div" className="relative z-10" onClose={cancelExcelUpload}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </Transition.Child>

                    <div className="fixed inset-0 z-10 overflow-y-auto">
                        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                enterTo="opacity-100 translate-y-0 sm:scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            >
                                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl">
                                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                        <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                                            Gebouwbestand importeren
                                        </Dialog.Title>
                                        <div className={importStep === 'file' ? 'mt-2' : 'hidden'}>
                                            <p className="text-sm text-gray-500">
                                                Laad het overzicht van het gebouw in (.xlsx) en kies het werkblad met de kavels en stemhouders.
                                                Per e-mailadres wordt één gebruiker aangemaakt als die nog niet bestaat, en één uitnodiging met de som van de aandelen van al zijn kavels.
                                                Wie al uitgenodigd is, behoudt zijn uitnodiging; enkel de aandelen worden bijgewerkt.
                                            </p>
                                            <div className="mt-4">
                                                <ExcelImport fields={MEETING_FIELDS} onChange={setImportRows} />
                                            </div>
                                        </div>
                                        {importStep !== 'file' ? (
                                            <div className="mt-2">
                                                <p className="text-sm text-gray-500">
                                                    {importStep === 'preview'
                                                        ? 'Controleer het overzicht. Er is nog niets opgeslagen.'
                                                        : 'De import is uitgevoerd.'}
                                                </p>
                                                <div className="mt-4">
                                                    <ImportSummary result={importResult} meeting showSkipped={importStep === 'done'} />
                                                </div>
                                                {importStep === 'preview' ? (
                                                    <MissingEmails items={missingItems} fixes={emailFixes} onChange={setEmailFixes} invalidEmails={invalidFixes} meeting />
                                                ) : null}
                                            </div>
                                        ) : null}
                                    </div>
                                    {uploading ?
                                        <div className="mt-2 mb-4">
                                            <Spinner />
                                        </div>
                                        :
                                        <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                            {importStep === 'file' ? (
                                                <button
                                                    type="button"
                                                    className="inline-flex w-full justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 sm:ml-3 sm:w-auto sm:text-sm"
                                                    onClick={() => checkImport()}
                                                    disabled={!importRows}
                                                >
                                                    Controleren
                                                </button>
                                            ) : null}
                                            {importStep === 'preview' ? (
                                                <button
                                                    type="button"
                                                    className="inline-flex w-full justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 sm:ml-3 sm:w-auto sm:text-sm"
                                                    onClick={() => runImport()}
                                                    disabled={unresolvedMissing(missingItems, emailFixes).length > 0}
                                                >
                                                    Importeren
                                                </button>
                                            ) : null}
                                            {importStep === 'preview' ? (
                                                <button
                                                    type="button"
                                                    className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                                    onClick={() => setImportStep('file')}
                                                >
                                                    Terug
                                                </button>
                                            ) : null}
                                            <button
                                                type="button"
                                                className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                                onClick={() => cancelExcelUpload()}
                                            >
                                                Sluiten
                                            </button>
                                        </div>}
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>
            <Transition.Root show={noDocusignContract} as={Fragment}>
                <Dialog as="div" className="relative z-10" onClose={setNoDocusignContract}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </Transition.Child>

                    <div className="fixed inset-0 z-10 overflow-y-auto">
                        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                enterTo="opacity-100 translate-y-0 sm:scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            >
                                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                                    <div className="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
                                        <button
                                            type="button"
                                            className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                            onClick={() => setNoDocusignContract(false)}
                                        >
                                            <span className="sr-only">Close</span>
                                            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                        </button>
                                    </div>
                                    <div className="sm:flex sm:items-start">
                                        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                            <ExclamationTriangleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                                        </div>
                                        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                            <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                                                Contract niet gevonden
                                            </Dialog.Title>
                                            <div className="mt-2">
                                                <p className="text-sm text-gray-500">
                                                    Dit contract werd getekend in de vorige versie van Assemblo en deze uitnodiging werd overgezet via Excel.
                                                    <br /><br />Je vindt dit contract terug in de Airtable van de vorige versie van Assemblo.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                                        <button
                                            type="button"
                                            className="inline-flex w-full justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
                                            onClick={() => setNoDocusignContract(false)}
                                        >
                                            Melding sluiten
                                        </button>

                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>
            
            {/* CONFIRM DELETE OF A PARTICIPANT WHO SIGNED A CONTRACT */}
            <Transition.Root show={deleteDialogOpen} as={Fragment}>
                <Dialog as="div" className="relative z-10" onClose={setDeleteDialogOpen}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </Transition.Child>

                    <div className="fixed inset-0 z-10 overflow-y-auto">
                        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                enterTo="opacity-100 translate-y-0 sm:scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            >
                                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                                    <div className="sm:flex sm:items-start">
                                        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                            <ExclamationTriangleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                                        </div>
                                        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                            <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                                                Deelnemer met getekend contract verwijderen?
                                            </Dialog.Title>
                                            <div className="mt-2 space-y-2 text-sm text-gray-500">
                                                <p>
                                                    {deleteCandidate?.name} heeft voor deze meeting een contract getekend
                                                    {deleteCandidate?.contracts.length ? ` (${deleteCandidate.contracts.join(', ')})` : ''}.
                                                </p>
                                                <p>
                                                    Als je deze deelnemer verwijdert, verdwijnt ook de koppeling met dat contract uit Assemblo.
                                                    Het getekende document blijft bewaard in DocuSign.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                                        <button
                                            type="button"
                                            className="inline-flex w-full justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
                                            onClick={confirmDeleteInvite}
                                        >
                                            Verwijderen
                                        </button>
                                        <button
                                            type="button"
                                            className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
                                            onClick={() => setDeleteDialogOpen(false)}
                                        >
                                            Annuleren
                                        </button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>

            {/* PHONE MODAL */}
            <Transition.Root show={phoneModalOpen} as={Fragment}>
                <Dialog 
                    as="div" 
                    className="relative z-10" 
                    onClose={() => {}} // Prevent closing on outside click
                    static
                >
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </Transition.Child>

                    <div className="fixed inset-0 z-10 overflow-y-auto">
                        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                enterTo="opacity-100 translate-y-0 sm:scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            >
                                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-6 pt-6 pb-6 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md">
                                    <PhoneModal 
                                        phoneNumber={selectedPhone}
                                        investorName={selectedName}
                                        meetingDate={meeting.date}
                                        onCancel={handlePhoneModalCancel}
                                        onCallResult={handleCallResult}
                                    />
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>
        </main>
    )
}

const TableComponent = ({ invitees, setInvitees, viewers, signs, props, redirectEnvelope, sendMessage, refreshUser, deleteUserInvite, adminUpdateRSVP, adminUpdateShares, adminUpdateSyndesk, isVME, openPhoneModal, handleCallStart, handleCallReset, currentFilter, onFilterChange }) => {
    const [sortColumn, setSortColumn] = useState('user_attend.firstname');
    const [sortDirection, setSortDirection] = useState('asc');
    const [searchTerm, setSearchTerm] = useState('');

    const handleSort = (column) => {
        if (column === sortColumn) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortDirection('asc');
        }
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    const filteredInvitees = useMemo(() => {
        const searchTerms = searchTerm.toLowerCase().split(' ').filter(Boolean);

        return invitees.filter(invitee => {
            // Filter by user type
            if (currentFilter === 'investeerders' && invitee.user_attend.email.toLowerCase().endsWith('@upgrade-estate.be')) return false;
            if (currentFilter === 'medewerkers' && !invitee.user_attend.email.toLowerCase().endsWith('@upgrade-estate.be')) return false;
            
            // Filter by search term
            const fullName = `${invitee.user_attend.firstname.toLowerCase()} ${invitee.user_attend.lastname.toLowerCase()}`;
            const userEmail = invitee.user_attend.email.toLowerCase();
            const userPhone = invitee.user_attend.telephone.toLowerCase();

            return searchTerms.every(term =>
                fullName.includes(term) ||
                userEmail.includes(term) ||
                userPhone.includes(term)
            );
        });
    }, [invitees, searchTerm, currentFilter]);

    const sortedInvitees = useMemo(() => {
        const sorted = [...filteredInvitees].sort((a, b) => {
            if (a[sortColumn] < b[sortColumn]) return sortDirection === 'asc' ? -1 : 1;
            if (a[sortColumn] > b[sortColumn]) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
        return sorted;
    }, [filteredInvitees, sortColumn, sortDirection]);

    function updateShares(data, attend_id, save) {
        setInvitees(invitees => invitees.map(invitee => {
            if (invitee.id == attend_id) {
                invitee.shares = data;
            }
            return invitee;
        }
        ));
        if (save) {
            adminUpdateShares(attend_id, data);
        }
    }

    function updateSyndesk(attend_id, syndesk) {
        setInvitees(invitees => invitees.map(invitee => {
            if (invitee.id == attend_id) {
                invitee.syndesk = syndesk;
            }
            return invitee;
        }));
        adminUpdateSyndesk(attend_id, syndesk);
    }

    return (
        <div className="">
            <div className="mt-8 flex flex-col">
                <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                        <div className="overflow-visible relative shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                            <div className="p-4">
                                <input
                                    type="text"
                                    placeholder="Zoeken..."
                                    value={searchTerm}
                                    onChange={handleSearch}
                                    className="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-indigo-500 border-gray-300 rounded-md"
                                />
                                <div className="mt-3 flex space-x-2">
                                    <button
                                        onClick={() => onFilterChange('investeerders')}
                                        className={`px-3 py-1 rounded-md text-sm font-medium ${
                                            currentFilter === 'investeerders'
                                                ? 'bg-indigo-100 text-indigo-700'
                                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                        }`}
                                    >
                                        Investeerders
                                    </button>
                                    <button
                                        onClick={() => onFilterChange('medewerkers')}
                                        className={`px-3 py-1 rounded-md text-sm font-medium ${
                                            currentFilter === 'medewerkers'
                                                ? 'bg-indigo-100 text-indigo-700'
                                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                        }`}
                                    >
                                        MedeMerkers
                                    </button>
                                    <button
                                        onClick={() => onFilterChange('all')}
                                        className={`px-3 py-1 rounded-md text-sm font-medium ${
                                            currentFilter === 'all'
                                                ? 'bg-indigo-100 text-indigo-700'
                                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                        }`}
                                    >
                                        Alle genodigden
                                    </button>
                                </div>
                            </div>
                            <table className="min-w-full divide-y divide-gray-300">
                                <thead className="bg-gray-100">
                                    <tr>
                                        <th
                                            scope="col"
                                            className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 cursor-pointer"
                                            onClick={() => handleSort('user_attend.firstname')}
                                        >
                                            Naam {sortColumn === 'user_attend.firstname' && (sortDirection === 'asc' ? '↑' : '↓')}
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer"
                                            onClick={() => handleSort('user_attend.email')}
                                        >
                                            E-mail {sortColumn === 'user_attend.email' && (sortDirection === 'asc' ? '↑' : '↓')}
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer"
                                            onClick={() => handleSort('user_attend.telephone')}
                                        >
                                            Telefoon {sortColumn === 'user_attend.telephone' && (sortDirection === 'asc' ? '↑' : '↓')}
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer"
                                            onClick={() => handleSort('rsvp')}
                                        >
                                            Deelname {sortColumn === 'rsvp' && (sortDirection === 'asc' ? '↑' : '↓')}
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer"
                                            onClick={() => handleSort('shares')}
                                        >
                                            Aandelen {sortColumn === 'shares' && (sortDirection === 'asc' ? '↑' : '↓')}
                                        </th>
                                        {isVME && (
                                            <th scope="col" className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">
                                                Syndesk
                                            </th>
                                        )}
                                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                            Contract(en)
                                        </th>
                                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                            Opmerking<br />
                                            <span className="text-xs text-gray-400 font-thin">
                                                Voer geen persoonlijke gegevens in.
                                            </span>
                                        </th>
                                        <th scope="col" className="relative py-3.5 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                                            Acties
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white">
                                    {sortedInvitees.map((invitee, inviteeIdx) => (
                                        <tr key={invitee.id} className={inviteeIdx % 2 === 0 ? undefined : 'bg-gray-50'}>
                                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                                                <div>
                                                    <div className="font-medium text-gray-900">
                                                        {invitee.user_attend.firstname} {invitee.user_attend.lastname}
                                                    </div>
                                                    <div className="mt-1 flex gap-2">
                                                        {/* Status badge - only show for online users */}
                                                        {invitee.rsvp === "online" && (
                                                            <span className={`inline-block flex-shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${viewers.includes(invitee.user_attend.id.toString()) ? "text-green-800 bg-green-100" : "text-red-800 bg-red-100"}`}>
                                                                {viewers.includes(invitee.user_attend.id.toString()) ? "ONLINE" : "OFFLINE"}
                                                            </span>
                                                        )}
                                                        {/* Language badge */}
                                                        <span className={`inline-block flex-shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
                                                            invitee.user_attend.lang === "nl" ? "text-blue-800 bg-blue-100" :
                                                            invitee.user_attend.lang === "fr" ? "text-purple-800 bg-purple-100" :
                                                            invitee.user_attend.lang === "en" ? "text-indigo-800 bg-indigo-100" :
                                                            "text-gray-800 bg-gray-100"
                                                        }`}>
                                                            {invitee.user_attend.lang === "nl" ? "NL" : 
                                                             invitee.user_attend.lang === "fr" ? "FR" : 
                                                             invitee.user_attend.lang === "en" ? "EN" : "??"}
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{invitee.user_attend.email}</td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                <div className="flex items-center gap-2">
                                                    <span 
                                                        className={`transition-all ${
                                                            (invitee.call_status === 'red' || invitee.call_status === 'orange') 
                                                                ? 'blur-sm hover:blur-none' 
                                                                : ''
                                                        }`}
                                                    >
                                                        {invitee.user_attend.telephone}
                                                    </span>
                                                    <CallButton 
                                                        invitee={invitee}
                                                        handleCallStart={handleCallStart}
                                                    />
                                                </div>
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                {invitee.rsvp === "present" ? "Fysiek aanwezig" :
                                                    invitee.rsvp === "not" ? "Afwezig" :
                                                        invitee.rsvp === "proxy" ? "Volmacht" :
                                                            invitee.rsvp === "online" ? "Online volgen" : "Niet doorgegeven"}
                                            </td>
                                            <td className="whitespace-nowrap px-1 py-4 text-sm text-gray-500">
                                                <input
                                                    className="w-full border-none bg-transparent rounded-md"
                                                    type="text"
                                                    value={invitee.shares ? invitee.shares : "/"}
                                                    onChange={(e) => updateShares(e.target.value, invitee.id, false)}
                                                    onBlur={(e) => updateShares(e.target.value, invitee.id, true)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.target.blur();
                                                        }
                                                    }}
                                                />
                                            </td>
                                            {isVME && (
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={invitee.syndesk || false}
                                                        onChange={(e) => updateSyndesk(invitee.id, e.target.checked)}
                                                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded cursor-pointer"
                                                    />
                                                </td>
                                            )}
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                {signs.map(sign => (
                                                    sign.signage_attend.id === invitee.id ? (
                                                        <div
                                                            key={sign.id}
                                                            className='flex pb-2 cursor-pointer underline'
                                                            onClick={() => { redirectEnvelope(sign.envelope_id, invitee.user_attend.firstname, invitee.user_attend.lastname) }}
                                                        >
                                                            <DocumentMagnifyingGlassIcon className="h-5 w-5 text-gray-700" aria-hidden="true" />
                                                            <p>{sign.contract_signage.name}</p>
                                                        </div>
                                                    ) : null
                                                ))}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                <textarea
                                                    type="text"
                                                    rows={2}
                                                    className="w-full border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                                    value={invitee.comment}
                                                    onChange={(e) => { sendMessage(e.target.value, invitee.id, false) }}
                                                    onBlur={(e) => { sendMessage(e.target.value, invitee.id, true) }}
                                                />
                                            </td>
                                            <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                                                <Menu as="div" className="relative inline-block text-left">
                                                    <div>
                                                        <Menu.Button className="inline-flex justify-center w-full rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-100">
                                                            Acties
                                                            <ChevronDownIcon className="-mr-1 ml-2 h-4 w-4" aria-hidden="true" />
                                                        </Menu.Button>
                                                    </div>

                                                    <Transition
                                                        as={React.Fragment}
                                                        enter="transition ease-out duration-100"
                                                        enterFrom="transform opacity-0 scale-95"
                                                        enterTo="transform opacity-100 scale-100"
                                                        leave="transition ease-in duration-75"
                                                        leaveFrom="transform opacity-100 scale-100"
                                                        leaveTo="transform opacity-0 scale-95"
                                                    >
                                                        <Menu.Items className={`absolute right-0 z-50 w-56 divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none ${
                                                            inviteeIdx >= sortedInvitees.length - 3 ? 'bottom-full mb-2 origin-bottom-right' : 'mt-2 origin-top-right'
                                                        }`}>
                                                            <div className="py-1">
                                                                <Menu.Item>
                                                                    {({ active }) => (
                                                                        <p
                                                                            onClick={() => refreshUser(invitee.user_attend.id)}
                                                                            className={`${active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'} group flex items-center px-4 py-2 text-sm cursor-pointer`}
                                                                        >
                                                                            <ArrowPathIcon
                                                                                className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                                                                                aria-hidden="true"
                                                                            />
                                                                            Browser refreshen
                                                                        </p>
                                                                    )}
                                                                </Menu.Item>
                                                                <Menu.Item>
                                                                    {({ active }) => (
                                                                        <p
                                                                            onClick={() => adminUpdateRSVP(invitee.user_attend.id, "present")}
                                                                            className={`${active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'} group flex items-center px-4 py-2 text-sm cursor-pointer`}
                                                                        >
                                                                            <RectangleGroupIcon
                                                                                className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                                                                                aria-hidden="true"
                                                                            />
                                                                            RSVP: Aanwezig
                                                                        </p>
                                                                    )}
                                                                </Menu.Item>
                                                                <Menu.Item>
                                                                    {({ active }) => (
                                                                        <p
                                                                            onClick={() => adminUpdateRSVP(invitee.user_attend.id, "not")}
                                                                            className={`${active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'} group flex items-center px-4 py-2 text-sm cursor-pointer`}
                                                                        >
                                                                            <RectangleGroupIcon
                                                                                className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                                                                                aria-hidden="true"
                                                                            />
                                                                            RSVP: Afwezig
                                                                        </p>
                                                                    )}
                                                                </Menu.Item>
                                                                <Menu.Item>
                                                                    {({ active }) => (
                                                                        <p
                                                                            onClick={() => adminUpdateRSVP(invitee.user_attend.id, "proxy")}
                                                                            className={`${active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'} group flex items-center px-4 py-2 text-sm cursor-pointer`}
                                                                        >
                                                                            <RectangleGroupIcon
                                                                                className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                                                                                aria-hidden="true"
                                                                            />
                                                                            RSVP: Volmacht
                                                                        </p>
                                                                    )}
                                                                </Menu.Item>
                                                                <Menu.Item>
                                                                    {({ active }) => (
                                                                        <p
                                                                            onClick={() => adminUpdateRSVP(invitee.user_attend.id, "online")}
                                                                            className={`${active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'} group flex items-center px-4 py-2 text-sm cursor-pointer`}
                                                                        >
                                                                            <RectangleGroupIcon
                                                                                className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                                                                                aria-hidden="true"
                                                                            />
                                                                            RSVP: Online
                                                                        </p>
                                                                    )}
                                                                </Menu.Item>
                                                                {(invitee.call_status === 'red' || invitee.call_status === 'orange') && (
                                                                    <Menu.Item>
                                                                        {({ active }) => (
                                                                            <p
                                                                                onClick={() => handleCallReset(invitee.id, invitee.user_attend.id)}
                                                                                className={`${active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'} group flex items-center px-4 py-2 text-sm cursor-pointer`}
                                                                            >
                                                                                <ArrowPathIcon
                                                                                    className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                                                                                    aria-hidden="true"
                                                                                />
                                                                                Reset call status
                                                                            </p>
                                                                        )}
                                                                    </Menu.Item>
                                                                )}
                                                                <Menu.Item>
                                                                    {({ active }) => (
                                                                        <p
                                                                            onClick={() => deleteUserInvite(invitee.id)}
                                                                            className={`${active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'} group flex items-center px-4 py-2 text-sm cursor-pointer`}
                                                                        >
                                                                            <TrashIcon
                                                                                className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                                                                                aria-hidden="true"
                                                                            />
                                                                            Verwijderen
                                                                        </p>
                                                                    )}
                                                                </Menu.Item>
                                                            </div>
                                                        </Menu.Items>
                                                    </Transition>
                                                </Menu>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Call Button Component
const CallButton = ({ invitee, handleCallStart }) => {
    const callStatus = invitee.call_status || 'green'; // Default to green if not set

    const handleClick = () => {
        if (callStatus === 'green') {
            const fullName = `${invitee.user_attend.firstname} ${invitee.user_attend.lastname}`;
            handleCallStart(invitee.id, invitee.user_attend.id, invitee.user_attend.telephone, fullName);
        } else if (callStatus === 'orange' && invitee.previous_call) {
            const callTime = new Date(invitee.previous_call.called_at).toLocaleTimeString('nl-NL', {
                hour: '2-digit',
                minute: '2-digit'
            });
            toast.info(`Gebeld in ${invitee.previous_call.meeting_title} om ${callTime}`);
        }
    };

    const getButtonClass = () => {
        switch (callStatus) {
            case 'green':
                return 'bg-green-600 hover:bg-green-700 cursor-pointer';
            case 'red':
                return 'bg-red-600 cursor-not-allowed opacity-60';
            case 'orange':
                return 'bg-orange-600 hover:bg-orange-700 cursor-pointer';
            default:
                return 'bg-green-600 hover:bg-green-700 cursor-pointer';
        }
    };

    const getTooltipText = () => {
        if (callStatus === 'orange' && invitee.previous_call) {
            const callTime = new Date(invitee.previous_call.called_at).toLocaleTimeString('nl-NL', {
                hour: '2-digit',
                minute: '2-digit'
            });
            return `Gebeld in ${invitee.previous_call.meeting_title} om ${callTime}`;
        }
        return '';
    };

    return (
        <button
            onClick={handleClick}
            disabled={callStatus === 'red'}
            className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${getButtonClass()}`}
            title={getTooltipText()}
        >
            Bel
        </button>
    );
};

// Phone Modal Component
const PhoneModal = ({ phoneNumber, investorName, meetingDate, onCancel, onCallResult }) => {
    const [timeUntilMeeting, setTimeUntilMeeting] = useState('');

    // Calculate countdown
    useEffect(() => {
        const updateCountdown = () => {
            if (!meetingDate) return;
            
            const now = new Date();
            const meetingTime = new Date(meetingDate);
            const diffMs = meetingTime - now;
            
            if (diffMs > 0) {
                const minutes = Math.floor(diffMs / (1000 * 60));
                const hours = Math.floor(minutes / 60);
                const days = Math.floor(hours / 24);
                
                if (days > 0) {
                    setTimeUntilMeeting(`${days} dagen`);
                } else if (hours > 0) {
                    setTimeUntilMeeting(`${hours} uur en ${minutes % 60} minuten`);
                } else {
                    setTimeUntilMeeting(`${minutes} minuten`);
                }
            } else {
                setTimeUntilMeeting('Meeting is gestart');
            }
        };

        updateCountdown();
        const interval = setInterval(updateCountdown, 60000); // Update every minute
        
        return () => clearInterval(interval);
    }, [meetingDate]);

    return (
        <div className="text-center">
            {/* Investor Name */}
            <div className="mb-4">
                <h1 className="text-xl font-semibold text-gray-900 mb-2">
                    {investorName}
                </h1>
            </div>
            
            {/* Phone Number - Large */}
            <div className="mb-4">
                <div className="flex items-center justify-center gap-2 mb-2">
                    <h2 className="text-2xl font-bold text-gray-900">
                        {phoneNumber}
                    </h2>
                    <a
                        href={`tel:${phoneNumber}`}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-green-100 text-green-600 hover:bg-green-200 transition-colors"
                        title="Direct bellen"
                    >
                        <PhoneIcon className="h-4 w-4" />
                    </a>
                </div>
                {/* Meeting Countdown */}
                <p className="text-sm text-gray-500">
                    Meeting start over: {timeUntilMeeting}
                </p>
            </div>

            {/* Call Result Buttons */}
            <div className="mb-6 space-y-3">
                <button
                    onClick={() => onCallResult('voicemail')}
                    className="w-full inline-flex justify-center items-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                >
                    Voicemail
                </button>
                
                <button
                    onClick={() => onCallResult('afwezig')}
                    className="w-full inline-flex justify-center items-center rounded-md border border-transparent bg-yellow-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2"
                >
                    Afwezig
                </button>
                
                <button
                    onClick={() => onCallResult('aanwezig')}
                    className="w-full inline-flex justify-center items-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                >
                    Aanwezig
                </button>
            </div>

            {/* Cancel Button */}
            <button
                onClick={onCancel}
                className="w-full inline-flex justify-center items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
                Annuleren
            </button>
        </div>
    );
};